import type { Messages } from '../messages.js'

/**
 * Vietnamese. `nước` for flips, the word a Vietnamese board game uses for a move.
 *
 * Vietnamese has one plural form and no agreement, so every count here is a bare numeral and a
 * classifier where the noun takes one: `3 từ`, `5 nước`.
 *
 * `htBoardBody` carries a sentence the other locales have no need of. Tiles hold their tone —
 * À and Á are different tiles, as they are in Vietboard — and the space between syllables is
 * not a tile at all, so SINH VIÊN is played as six tiles with no gap. A player who has not been
 * told will look for a space tile and not find one.
 */
export const vi: Messages = {
  tag: 'vi',

  readingDictionary: 'Đang đọc từ điển…',
  noWordList: 'Không có danh sách từ cho "{language}". Hãy tạo một:  pnpm dictionary build',
  emptyWordList: 'Danh sách từ của "{language}" trống.',

  flips: 'nước',
  score: 'điểm',
  words: 'từ',
  round: 'vòng',
  ticksLeftLabel: 'Thời gian còn lại trong vòng này',
  typeAWord: 'nhập một từ',
  tapPrompt: 'chạm vào chữ để chọn hoặc trả lại, rồi {action}',

  boardOfTiles: 'Bàn gồm {n} quân',
  faceDown: 'úp xuống',
  wildCard: 'quân trắng',
  wildKey: 'chữ bất kỳ',
  letterReplaced: '{from} đã thành {to}',
  letterSwap: 'ĐỔI CHỮ!',
  spentTile: 'quân đã dùng',
  hiddenWhilePaused: 'ẩn khi tạm dừng',
  letterInWord: '{letter}, chữ thứ {position} của từ',

  completeWord: 'Hoàn thành từ',

  completeShort: 'Xong',
  reset: 'Xóa',
  pause: 'Tạm dừng',
  resume: 'Tiếp tục',
  newGame: 'Ván mới',
  paused: 'Đang tạm dừng',
  outOfFlips: 'Hết nước',
  finalResult: '{score} cho {words} qua {rounds}',
  playAgain: 'Chơi lại',
  share: 'Chia sẻ',
  shareCopied: 'Đã sao chép.',
  shareSelect: 'Sao chép cái này:',

  lettersSelect: 'chữ để chọn',
  keysWild: 'được lấy khi bạn gõ một chữ không quân nào đang hiện',
  clearsEvery: 'xóa mọi {letter} đã chọn',
  undoLastLetter: 'hoàn tác chữ cuối',
  noWordsYet: 'Chưa có từ nào.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'đã tìm được rồi',
  reasonTooShort: 'quá ngắn',
  reasonNotAWord: 'không phải là từ',
  reasonAllFound: 'bạn đã có đủ cả',
  noSuchLetterUp: 'không có {letter} nào đang mở',
  nothingUp: 'chưa mở quân nào',
  letterHidden: 'một chữ đã ẩn đi',
  shuffled: 'đã xáo',
  shuffledAndBilled: 'đã xáo, trừ {flips} chưa dùng',
  tooFewLetters: 'Còn quá ít chữ cái',

  gameLanguage: 'ngôn ngữ',
  interfaceLanguage: 'giao diện',
  dictionarySize: '{common} từ thường dùng trong {full}',
  filterLanguages: 'Tìm ngôn ngữ',
  noMatches: 'Không có kết quả',

  nerdMode: 'chế độ chuyên sâu',
  rules: 'Luật chơi',
  difficulty: 'độ khó',
  difficultyNames: { easy: 'dễ', medium: 'vừa', hard: 'khó', insane: 'điên rồ' },
  pausedNoBoard: 'Lượt chơi này sẽ không lên bảng xếp hạng.',
  about: 'Giới thiệu',
  themeLabel: 'chủ đề',
  themeNames: { traditional: 'truyền thống', light: 'sáng', contrast: 'tương phản cao' },
  tiles: 'quân (N)',
  secondsPerTick: 'giây / nhịp',
  holdTicks: 'nhịp giữ',
  minWord: 'từ ngắn nhất',
  startingFlips: 'nước khởi đầu',
  wildChance: 'tỉ lệ quân trắng',
  replaceChance: 'tỉ lệ đổi chữ',
  hideChance: 'tỉ lệ ẩn chữ',
  wordCompleteMode: 'khi xong một từ',
  wordCompleteNames: { shuffle: 'xáo', spend: 'dùng hết', keep: 'giữ lại' },
  flipEconomy: 'hoàn nước',
  flipEconomyNames: {
    none: 'không',
    perLetter: 'theo từng chữ',
    fibonacci: 'fibonacci',
    overMinimum: 'vượt mức tối thiểu',
  },
  repeatedLetterKey: 'phím chữ lặp',
  keySchemeNames: { cycle: 'vòng', advance: 'tiến' },
  keySchemeHelp: {
    cycle:
      'A lấy quân A chưa dùng tiếp theo, và khi tất cả đã vào từ thì xóa hết. ' +
      'Shift+A cũng xóa hết.',
    advance: 'A lấy quân A chưa dùng tiếp theo. Shift+A xóa mọi A trong từ.',
  },

  whatThatMeans: 'Nghĩa là gì',
  factRound: 'vòng',
  factWholeBoardUp: 'cả bàn mở trong',
  factRoundCosts: 'một vòng tốn',
  factFlipsBuy: 'nước khởi đầu mua được',
  factThisBoard: 'bàn này',
  factBoardHadToAdmit: 'bàn phải nhận',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, dài nhất {longest}',
  wordsIncludingOneOf: '{words}, trong đó một từ {ceiling}',
  scorelessRounds: '{rounds} không điểm',

  whatAWordPays: 'Một từ đáng bao nhiêu',
  columnLetters: 'chữ',
  columnCost: 'phí',
  columnPoints: 'điểm',
  columnFlips: 'nước',
  columnNet: 'chênh lệch',

  canonicalRules: 'Luật chuẩn của mức {difficulty}.',
  customRules: 'Đã đổi khác mặc định. Điểm với luật riêng không vào bảng xếp hạng.',
  applyAndStart: 'Áp dụng và bắt đầu ván mới',
  changesNextGame: 'Thay đổi có hiệu lực từ ván sau.',
  presets: 'Mặc định:',

  start: 'Bắt đầu',
  restart: 'Chơi lại từ đầu',
  quit: 'Thoát',
  quitTitle: 'Thoát ván này?',
  restartTitle: 'Chơi lại ván này từ đầu?',
  restartConfirm: 'Từ đầu',
  quitConfirm: 'Thoát',
  keepPlaying: 'Chơi tiếp',
  personalBest: 'Những ván hay nhất của bạn',
  newPersonalBest: 'Kỷ lục mới của bạn.',
  columnRank: '#',
  notRanked: 'Luật riêng, nên ván này không vào bảng xếp hạng.',
  notRankedPaused: 'Đồng hồ đã dừng, nên ván này không vào bảng xếp hạng.',
  rankOfTotal: '{rank} trên {total}',

  howToPlay: 'Cách chơi',

  backToGame: 'Trở lại ván chơi',
  welcomeTitle: 'Chào mừng đến với Blinkered',
  tutorialSkip: 'Bỏ qua',
  tutorialNext: 'Tiếp',
  tutorialBack: 'Lùi',
  tutorialStart: 'Bắt đầu chơi',
  tutorialHideAgain: 'Đừng hiện lại nữa',
  tutorialProgress: '{n} trên {total}',
  tutorialSkipTitle: 'Bỏ qua phần giới thiệu?',
  tutGoalTitle: 'Blinkered là một cuộc đua',
  tutGoalBody:
    'Bạn bắt đầu ván với một số nước cố định, và mỗi quân mở ra tốn một nước. Khi hết nước là hết ván. Ghép từ sẽ được thêm nước, và từ càng dài thì càng được nhiều nước.',
  tutSpentBody:
    'Những chữ bạn dùng trong một từ sẽ bị úp xuống cho đến vòng sau. Chỉ có đổi chữ mới thay đổi các chữ trên bàn.',
  tutChangeTitle: 'Chữ có thể thay đổi',
  tutSameLetters:
    'Thấy một từ mà chưa kịp chơi? Những chữ đó sẽ quay lại ở vòng sau, được xáo vào chỗ mới.',
  tutPickLetters: 'Chạm vào các chữ bạn muốn, theo thứ tự, để ghép thành từ.',
  tutMoreTurn: 'Quân vẫn tiếp tục mở trong lúc bạn nghĩ, nên chữ tốt hơn có thể vẫn đang tới.',
  tutTapBack: 'Lỡ lấy chữ không muốn? Chạm lại để trả về. Chữ nào cũng được, không riêng chữ cuối.',
  tutComplete: 'Nhấn Xong khi từ đã sẵn sàng.',
  htBoardTitle: 'Bàn chơi',
  htBoardBody:
    'Các quân mở lần lượt, theo thứ tự ngẫu nhiên. Ghép từ bằng những quân đã mở. Dấu thanh nằm ngay trên quân, nên À và Á là hai quân khác nhau. Khoảng trắng giữa các tiếng thì không phải là quân: SINH VIÊN ghép bằng sáu quân, không có chỗ trống.',
  htWordsTitle: 'Các từ',
  htWordsBody: 'Ghép một từ từ những quân đang mở bằng cách gõ hoặc bấm các chữ theo thứ tự.',
  htFlipsTitle: 'Nước',
  htFlipsBody:
    'Mỗi quân mở ra tốn một nước. Một từ hoàn thành hoàn lại nước, và từ càng dài hoàn lại càng nhiều. Khi hết nước là hết ván.',
  htRoundTitle: 'Vòng',
  htRoundBody:
    'Khi quân cuối cùng của vòng mở ra, cả bàn dừng lại một lát. Rồi các quân được úp xuống và xáo lại, và một vòng mới bắt đầu.',
  htLanguagesTitle: 'Các ngôn ngữ',
  htLanguagesBody:
    'Có {n} ngôn ngữ. Bàn nào cũng giải được bằng những từ người ta thật sự dùng. Từ hiếm vẫn được điểm, nếu từ điển biết nó.',
  htKeysTitle: 'Bàn phím',
  htWildTitle: 'Quân trắng',
  htWildBody:
    'Đôi khi một quân trắng hiện ra thay cho một chữ. Quân trắng tính là bất kỳ chữ nào tạo thành từ đúng. Từ đã ghép rồi thì không tính.',
  htHideTitle: 'Chữ ẩn đi',
  htHideBody:
    'Ở các mức khó, một chữ đang mở có thể tự úp lại và mở ra muộn hơn trong cùng vòng. Đồng hồ được thêm một vạch cho việc đó, màu đỏ. Những chữ đã chọn thì không úp lại.',
  tutHideBody:
    'Ở các mức khó, một chữ đang mở có thể tự úp lại và mở ra muộn hơn trong cùng vòng. Những chữ đã chọn thì không úp lại.',
  htSwapTitle: 'Chữ đổi',
  htSwapBody:
    'Đôi khi giữa hai vòng, một chữ được thay bằng chữ khác. Bạn sẽ thấy chữ nào đi và chữ nào đến.',
  htLevelsTitle: 'Các mức',
  htLevelEasy:
    'Vẫn mười hai chữ đó suốt ván, nên bạn có thể thuộc chúng và giữ sẵn một danh sách từ trong đầu. Quân mở chậm, và cả bàn còn hiện đủ lâu để bạn chọn xong.',
  htLevelMedium:
    'Thỉnh thoảng một chữ đổi, nên khó nhớ những từ để dành cho sau. Ít thời gian nhìn hơn, và ít thời gian nghĩ hơn.',
  htLevelHard:
    'Từ ba chữ không còn tính, và cứ khoảng hai vòng lại đổi một chữ. Bàn vừa kịp hiện ra là đã xáo.',
  htLevelInsane: 'Tất cả cùng lúc, hết tốc lực. Bàn xáo gần như ngay sau nước cuối.',
  htTouchTitle: 'Màn hình cảm ứng',
  htTouchBody:
    'Chạm vào một quân đang mở để lấy chữ của nó. Chạm vào chữ đã lấy để trả lại. Xong và Xóa nằm dưới bàn.',

  accountTitle: 'Tài khoản của bạn',
  menuProfile: 'Hồ sơ của tôi',
  menuGames: 'Ván của tôi',
  menuPublicPage: 'Trang công khai của tôi',
  menuSignedInAs: 'Đang đăng nhập với',
  signIn: 'Đăng nhập',
  signOut: 'Đăng xuất',
  signInTitle: 'Đăng nhập hoặc đăng ký',
  signInLead: 'Tài khoản giữ lại các ván của bạn, trên mọi thiết bị.',
  signInKeepGame: 'Đăng nhập để giữ ván này, và mọi ván sau đó.',
  continueWithApple: 'Tiếp tục với Apple',
  continueWithGoogle: 'Tiếp tục với Google',
  signInOr: 'hoặc',
  emailLabel: 'Email',
  codeLabel: 'Mã',
  emailMeACode: 'Gửi mã cho tôi',
  working: 'Chờ chút…',
  sendNewCode: 'Gửi mã mới',
  codeSent: 'Xem email của bạn. Mã có hiệu lực 10 phút.',
  notNow: 'Để sau',
  badEmail: 'Cái này không giống địa chỉ email.',
  badCode: 'Mã đó không dùng được. Mã hết hạn sau 10 phút và chỉ dùng một lần.',
  serverBusy: 'Không liên hệ được máy chủ. Thử lại sau chốc lát.',
  serverDown: 'Không liên hệ được máy chủ.',
  offline: 'Ngoại tuyến',
  leaderboardTitle: 'Bảng xếp hạng',
  leaderboardEmpty: 'Chưa có trận nào trên bảng này. Hãy là người đầu tiên.',
  leaderboardWouldBe: 'Trận này sẽ đứng ở đâu',
  leaderboardThisGame: 'Trận này',
  leaderboardRanksHere: 'Trận này đứng ở đâu',
  leaderboardOutside: 'Ngoài top 5.',
  tutAccountTitle: 'Giữ lại điểm của bạn',
  tutAccountBody:
    'Tài khoản lưu các trận của bạn, đưa điểm lên bảng xếp hạng và giữ hồ sơ của bạn.',
  ssoCancelled: 'Đã hủy đăng nhập.',
  ssoExpired: 'Lâu quá. Đăng nhập lại đi.',
  ssoNoUsername: 'Chưa tạo xong tài khoản. Thử lại sau chốc lát.',
  ssoFailed: 'Không được. Thử lại, hoặc dùng email.',
  tabProfile: 'Hồ sơ',
  countryAny: 'Không nói',
  findCountry: 'Tìm quốc gia',
  usernameLabel: 'Tên người dùng',
  bioLabel: 'Giới thiệu',
  countryLabel: 'Quốc gia',
  bioHint: 'Không liên kết. {left}/{max}',
  save: 'Lưu',
  saving: 'Đang lưu…',
  saved: 'Đã lưu.',
  nameTaken: 'Tên đó có người dùng rồi.',
  nameTooShort: 'Dài thêm chút nữa.',
  nameTooLong: 'Dài quá.',
  nameBadCharacters: 'Chỉ chữ, số, gạch nối và gạch dưới.',
  nameBadEdges: 'Phải bắt đầu và kết thúc bằng chữ hoặc số.',
  nameMixedScripts: 'Chọn một hệ chữ và giữ nguyên.',
  nameReserved: 'Giống tên do chúng tôi phát. Chọn tên khác.',
  nameUnusable: 'Không dùng được tên đó.',
  bioTooLong: 'Phần giới thiệu tối đa {max} ký tự.',
  bioHasLink: 'Không được đặt liên kết trong phần giới thiệu.',
  bioHasControl: 'Có ký tự mà phần giới thiệu không chứa được.',
  bioUnusable: 'Không dùng được phần giới thiệu đó.',
  gamesHeading: 'Ván',
  gamesEmpty: 'Chưa có gì ở đây. Ván bạn chơi khi đã đăng nhập sẽ hiện ở đây.',
  gamesEmptyShort: 'Chưa có gì ở đây.',
  gamesLoading: 'Đang đọc các ván của bạn…',
  theirGamesLoading: 'Đang đọc các ván của họ…',
  profileLoading: 'Đang đọc hồ sơ…',
  playerNotFound: 'Không có ai ở đây tên vậy.',
  columnWhen: 'Khi nào',
  columnRounds: 'vòng',
  columnGame: 'Ván',
  gameLoading: 'Đang đọc ván…',
  gameUnreadable: 'Không đọc được ván này.',
  gameNotRanked: 'Chơi với luật đã sửa, nên ván này không xếp hạng.',
  detailNotKept: 'Từ và bảng của ván này không còn được giữ.',
  openingBoard: 'Bảng mở đầu',
  roundByRound: 'Từng vòng',
  roundNothing: 'không tìm được gì',
  changeReplaced: '{letter} được thay ở ô {slot}',
  changeWildOn: '{letter} thành lá joker ở ô {slot}',
  changeWildOff: '{letter} mất lá joker ở ô {slot}',
  keepThisGame: 'Giữ ván này',
  savedToYourGames: 'Đã lưu vào các ván của bạn.',

  reportThis: 'Báo cáo',
  reportHeading: 'Báo cáo điều này',
  reportTheName: 'Tên',
  reportTheBio: 'Giới thiệu',
  reportTheScore: 'Điểm',
  reportWhy: 'Có gì sai với nó? Không bắt buộc.',
  reportSend: 'Gửi báo cáo',
  reportSent: 'Cảm ơn bạn. Sẽ có người xem.',
  reportSignIn: 'Đăng nhập để báo cáo điều gì đó.',
  reportGone: 'Cái đó không còn ở đó nữa.',
  reportCancel: 'Huỷ',

  deleteAccount: 'Xoá tài khoản của tôi',
  deleteAccountWhat:
    'Việc này xoá vĩnh viễn trang cá nhân của bạn và mọi trận đã lưu. Không thể hoàn tác.',
  deleteAccountAsk: 'Gửi cho tôi một mã',
  deleteConfirm: 'Xoá vĩnh viễn',
  deleteGone: 'Tài khoản của bạn đã bị xoá. Cảm ơn bạn đã chơi.',
  deleteNoAddress:
    'Tài khoản này không có địa chỉ đã xác nhận, nên không thể kiểm tra việc xoá. Hãy liên hệ và chúng tôi sẽ làm thủ công.',

  rulesetFacts: 'Từ có {letters} · mỗi {seconds} mở một quân mới',
  plurals: {
    words: { other: '{n} từ' },
    rounds: { other: '{n} vòng' },
    flips: { other: '{n} nước' },
    ticks: { other: '{n} nhịp' },
    points: { other: '{n} điểm' },
    minLetters: { other: '{n} chữ trở lên' },
    seconds: { other: '{n} giây' },
  },
}
